/**
 * The named colour gradients of the legacy app (`ColorGenerator.cs`): lists of colours that run
 * through hues at full saturation and half lightness, used by the gauge.
 */

/** Hue gradient: `count` colours from `startHue` towards `endHue`, end excluded, as the legacy code makes them. */
export function hueGradient(startHue: number, endHue: number, count: number) : string[] {
    const colors: string[] = [];
    for (let index = 0; index < count; index++) {
        const hue = startHue + (index * (endHue - startHue)) / count;
        colors.push(`hsl(${hue}, 100%, 50%)`);
    }

    return colors;
}

type Segment = readonly [startHue: number, endHue: number, count: number];

const NAMED_GRADIENTS: Readonly<Record<string, readonly Segment[]>> = {
    'red-green': [[0, 120, 256]],
    'green-red': [[120, 0, 256]],
    'blue-red': [[240, 0, 256]],
    'red-blue': [[0, 240, 256]],
    'green-blue': [[120, 240, 256]],
    'blue-green': [[240, 120, 256]],
    'red-yellow-green': [[0, 120, 128], [120, 60, 128]],
    'green-yellow-red': [[120, 60, 128], [60, 0, 128]],
    'blue-yellow-red': [[240, 60, 128], [60, 0, 128]],
    'red-yellow-blue': [[0, 60, 128], [60, 240, 128]],
    'green-yellow-blue': [[120, 60, 128], [60, 240, 128]],
    'blue-yellow-green': [[240, 60, 128], [60, 120, 128]],
    'red-yellow-green-blue': [[0, 60, 64], [60, 120, 64], [120, 240, 64], [240, 0, 64]],
    'green-yellow-red-blue': [[120, 60, 64], [60, 0, 64], [0, 240, 64], [240, 120, 64]],
    'blue-yellow-red-green': [[240, 60, 64], [60, 0, 64], [0, 120, 64], [120, 240, 64]],
    'red-green-yellow-blue': [[0, 120, 64], [120, 60, 64], [60, 240, 64], [240, 0, 64]],
    'green-red-yellow-blue': [[120, 0, 64], [0, 60, 64], [60, 240, 64], [240, 120, 64]],
    'blue-red-yellow-green': [[240, 0, 64], [0, 60, 64], [60, 120, 64], [120, 240, 64]],
};

/** The gradients by name, for the editor's choice (T-4). */
export const GRADIENT_NAMES: readonly string[] = Object.keys(NAMED_GRADIENTS);

/** The default of the legacy gauge when no colours are given. */
export const DEFAULT_GAUGE_GRADIENT = 'red-green';

/** A named gradient; throws for an unknown name, as the legacy code does. */
export function namedGradient(name: string) : string[] {
    const segments = NAMED_GRADIENTS[name];
    if (!segments) {
        throw new Error(`unknown colour range: ${name}`);
    }

    return segments.flatMap(([startHue, endHue, count]) => hueGradient(startHue, endHue, count));
}

/** A list of colours as it is, or the colours of a named gradient. */
export function gradientColors(colors: string | readonly string[]) : string[] {
    return typeof colors === 'string' ? namedGradient(colors) : [...colors];
}

/** A hue at full saturation and half lightness, as a named gradient's colours are, as `#rrggbb`. */
export function hueHex(hue: number) : string {
    const channel = (n: number) : string => {
        const k = (n + hue / 30) % 12;
        const value = 0.5 - 0.5 * Math.max(-1, Math.min(k - 3, 9 - k, 1));
        return Math.round(value * 255).toString(16).padStart(2, '0');
    };

    return `#${channel(0)}${channel(8)}${channel(4)}`;
}

/**
 * A gradient's first colour as a Tilez colour (`#rrggbb` for a named one, whose colours are `hsl()`),
 * for one colour taken from it (issue #24); undefined for an unknown name or an empty list.
 */
export function firstGradientColor(colors: string | readonly string[]) : string | undefined {
    if (typeof colors === 'string') {
        const first = NAMED_GRADIENTS[colors]?.[0];
        return first ? hueHex(first[0]) : undefined;
    }

    return colors[0];
}

/** `count` colours picked evenly from `colors` (the legacy `CreateRedYellowGreenGradient`). */
export function sampleColors(colors: readonly string[], count: number) : string[] {
    const sampled: string[] = [];
    for (let index = 0; index < count; index++) {
        sampled.push(colors[Math.floor((index * colors.length) / count)] ?? 'white');
    }

    return sampled;
}
